import React, { useState, useEffect, useCallback } from 'react';
import { useAuth, useLanguage, api } from '../context/AppContext';
import { Button } from './ui/button';
import { X, Activity, HeartPulse, CheckCircle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import { getLocalDateString } from '../utils/dateUtils';

const STORAGE_KEY = 'yumix_pain_check_date';

const ZONE_ICONS = {
  epaule: '💪', bas_du_dos: '🔙', genou: '🦵', hanche: '🦴',
  poignet: '✋', cheville: '🦶', cou_cervicales: '🧣', coude: '💪',
};

const PainCheckPopup = () => {
  const { user } = useAuth();
  const { language } = useLanguage();
  const t = (fr, en) => (language === 'fr' ? fr : en);

  const [pendingZones, setPendingZones] = useState([]);
  const [currentIdx, setCurrentIdx] = useState(0);
  const [selectedScore, setSelectedScore] = useState(null);
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [completed, setCompleted] = useState(false);
  const [loading, setLoading] = useState(true);

  const fetchCheck = useCallback(async () => {
    if (!user) return;
    const today = getLocalDateString();
    const lastCheck = localStorage.getItem(STORAGE_KEY);
    if (lastCheck === today) {
      setLoading(false);
      return;
    }
    try {
      const res = await api.get('/injuries/pain-check');
      if (res.data.needs_check && res.data.pending_zones?.length > 0) {
        setPendingZones(res.data.pending_zones);
      }
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => { fetchCheck(); }, [fetchCheck]);

  const submitScore = async () => {
    if (selectedScore === null) return;
    setSaving(true);
    const zone = pendingZones[currentIdx];
    try {
      await api.post('/injuries/pain-log', {
        zone: zone.zone,
        score: selectedScore,
        notes: notes || undefined,
      });

      if (currentIdx < pendingZones.length - 1) {
        setCurrentIdx(prev => prev + 1);
        setSelectedScore(null);
        setNotes('');
      } else {
        setCompleted(true);
        localStorage.setItem(STORAGE_KEY, getLocalDateString());
        toast.success(t('SITREP enregistre!', 'SITREP logged!'));
        setTimeout(() => setDismissed(true), 2000);
      }
    } catch {
      toast.error(t('Erreur', 'Error'));
    } finally {
      setSaving(false);
    }
  };

  const dismiss = () => {
    localStorage.setItem(STORAGE_KEY, getLocalDateString());
    setDismissed(true);
  };

  if (loading || dismissed || pendingZones.length === 0) return null;

  const currentZone = pendingZones[currentIdx];
  const icon = ZONE_ICONS[currentZone?.zone] || '🩹';
  const zoneLabel = language === 'fr' ? currentZone?.label_fr : currentZone?.label_en;

  if (completed) {
    return (
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0 }}
        className="rounded-xl border border-emerald-500/40 bg-gradient-to-br from-emerald-500/10 via-card to-green-500/5 p-4"
        data-testid="pain-check-complete"
      >
        <div className="flex items-center gap-3">
          <CheckCircle className="w-5 h-5 text-emerald-400" />
          <div>
            <p className="text-sm font-heading uppercase text-emerald-300 tracking-tight">
              {t('SITREP Complete', 'SITREP Complete')}
            </p>
            <p className="text-xs text-muted-foreground mt-0.5">
              {t('Rapport de douleur enregistre. Continue ton combat!', 'Pain report logged. Keep fighting!')}
            </p>
          </div>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      className="rounded-xl border-2 border-orange-500/40 bg-gradient-to-br from-orange-500/10 via-card to-red-500/5 overflow-hidden"
      data-testid="pain-check-popup"
    >
      {/* Header */}
      <div className="p-4 pb-3">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <HeartPulse className="w-5 h-5 text-orange-400" />
            <span className="font-heading text-xs uppercase tracking-widest text-orange-400">
              SITREP — {t('Journal de Readaptation', 'Recovery Journal')}
            </span>
            <span className="text-[9px] px-1.5 py-0.5 bg-orange-500/20 text-orange-300 rounded-full">
              {currentIdx + 1}/{pendingZones.length}
            </span>
          </div>
          <button
            onClick={dismiss}
            className="p-1 text-muted-foreground hover:text-foreground rounded transition-colors"
            data-testid="pain-check-dismiss"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Zone being evaluated */}
        <div className="flex items-center gap-3 mb-4">
          <span className="text-3xl">{icon}</span>
          <div>
            <p className="text-sm text-foreground/90">
              {t(
                `Comment est ta douleur a la zone ${zoneLabel} aujourd'hui?`,
                `How is your ${zoneLabel} pain today?`
              )}
            </p>
            <p className="text-[10px] text-muted-foreground mt-0.5">
              {t('1 = Aucune douleur  →  10 = Douleur intense', '1 = No pain  →  10 = Intense pain')}
            </p>
          </div>
        </div>

        {/* Pain scale 1-10 */}
        <div className="grid grid-cols-10 gap-1.5 mb-3" data-testid="pain-scale">
          {Array.from({ length: 10 }, (_, i) => i + 1).map(score => {
            const color = score <= 3
              ? 'bg-emerald-500/20 text-emerald-300 border-emerald-500/40 hover:bg-emerald-500/30'
              : score <= 5
              ? 'bg-yellow-500/20 text-yellow-300 border-yellow-500/40 hover:bg-yellow-500/30'
              : score <= 7
              ? 'bg-orange-500/20 text-orange-300 border-orange-500/40 hover:bg-orange-500/30'
              : 'bg-red-500/20 text-red-300 border-red-500/40 hover:bg-red-500/30';

            const activeColor = score <= 3
              ? 'bg-emerald-500 text-white ring-2 ring-emerald-400/50'
              : score <= 5
              ? 'bg-yellow-500 text-black ring-2 ring-yellow-400/50'
              : score <= 7
              ? 'bg-orange-500 text-white ring-2 ring-orange-400/50'
              : 'bg-red-500 text-white ring-2 ring-red-400/50';

            return (
              <button
                key={score}
                onClick={() => setSelectedScore(score)}
                className={`aspect-square rounded-lg border flex items-center justify-center text-sm font-bold transition-all ${
                  selectedScore === score ? activeColor : color
                }`}
                data-testid={`pain-score-${score}`}
              >
                {score}
              </button>
            );
          })}
        </div>

        {/* Scale labels */}
        <div className="flex justify-between mb-3 px-0.5">
          <span className="text-[9px] text-emerald-400">{t('Aucune douleur', 'No pain')}</span>
          <span className="text-[9px] text-red-400">{t('Douleur intense', 'Intense pain')}</span>
        </div>

        {/* Optional notes */}
        {selectedScore !== null && (
          <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} className="mb-3">
            <input
              type="text"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder={t('Notes (optionnel)...', 'Notes (optional)...')}
              className="w-full px-3 py-2 rounded-lg bg-secondary border border-input text-sm text-foreground placeholder:text-muted-foreground"
              data-testid="pain-notes-input"
            />
          </motion.div>
        )}

        {/* Submit button */}
        <Button
          onClick={submitScore}
          disabled={selectedScore === null || saving}
          className="w-full"
          data-testid="pain-submit-btn"
        >
          {saving ? (
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <Activity className="w-4 h-4 mr-2" />
          )}
          {selectedScore !== null
            ? t(`Enregistrer ${selectedScore}/10`, `Log ${selectedScore}/10`)
            : t('Selectionne un score', 'Select a score')}
        </Button>
      </div>
    </motion.div>
  );
};

export default PainCheckPopup;
